import { type ReactNode, useEffect, useRef, useState } from 'react'

import { ALLOWED_COL_SPANS, ALLOWED_ROW_SPANS } from '../../../../../common/dashboard-cards.js'
import { navigateTo } from '../../../../../common/frontend/route-utils.js'
import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import { Button } from '../../../../components/button/button.js'
import { DragVerticalIcon } from '../../../../components/icon/icon.js'
import { MoreHorizontal, RefreshCw, Trash2 } from '../../../../components/icon/solar-icons.js'
import './card-frame.css'
import type { CardAction } from './cards/index.js'

interface Props {
    lang: Language
    title: string
    icon: ReactNode
    action?: CardAction | null
    state: 'idle' | 'loading' | 'error'
    editMode: boolean
    colSpan?: number
    rowSpan?: number
    onRetry?: () => void
    onRemove?: () => void
    onResize?: (patch: { colSpan?: number; rowSpan?: number }) => void
    dragHandleProps?: Record<string, unknown>
    children: ReactNode
}

export const CardFrame = ({
    lang,
    title,
    icon,
    action,
    state,
    editMode,
    colSpan,
    rowSpan,
    onRetry,
    onRemove,
    onResize,
    dragHandleProps,
    children,
}: Props) => (
    <div className={`portal-card${editMode ? ' portal-card--editing' : ''}`}>
        <div className="portal-card__header">
            {editMode && (
                <button
                    type="button"
                    className="portal-card__drag-handle"
                    aria-label={t.portal.dashboard.edit.dragToReorder(lang)}
                    {...(dragHandleProps ?? {})}
                >
                    <DragVerticalIcon size={16} />
                </button>
            )}
            <div className="portal-card__icon">{icon}</div>
            <div className="portal-card__title">{title}</div>
            {!editMode && action && state === 'idle' && (
                <Button
                    className="portal-card__action"
                    variant="secondary"
                    size="small"
                    text={action.label}
                    iconRight={action.icon}
                    {...(action.external
                        ? {
                              href: action.path,
                              anchorProps: { target: '_blank', rel: 'noreferrer' },
                          }
                        : { onClick: () => navigateTo(action.path) })}
                />
            )}
            {editMode && (
                <div className="portal-card__actions">
                    {onResize && (
                        <CardResizeMenu
                            lang={lang}
                            colSpan={colSpan}
                            rowSpan={rowSpan}
                            onResize={onResize}
                        />
                    )}
                    {onRemove && (
                        <button
                            type="button"
                            className="portal-card__remove"
                            aria-label={t.portal.dashboard.edit.removeCard(lang)}
                            onClick={onRemove}
                        >
                            <Trash2 size={14} weight="Linear" />
                        </button>
                    )}
                </div>
            )}
        </div>
        <hr className="portal-card__divider" />
        <div className="portal-card__body">
            {state === 'loading' && <div className="portal-card__skeleton" />}
            {state === 'error' && (
                <div className="portal-card__error">
                    <span>{t.portal.dashboard.edit.cardError(lang)}</span>
                    {onRetry && (
                        <button type="button" className="portal-card__retry" onClick={onRetry}>
                            <RefreshCw size={12} weight="Bold" />
                        </button>
                    )}
                </div>
            )}
            {state === 'idle' && children}
        </div>
    </div>
)

// Width options labelled by fraction of the 6-column grid.
const WIDTH_LABELS: Record<number, (lang: Language) => string> = {
    2: t.portal.dashboard.edit.widthThird,
    3: t.portal.dashboard.edit.widthHalf,
    4: t.portal.dashboard.edit.widthTwoThirds,
    6: t.portal.dashboard.edit.widthFull,
}

const HEIGHT_LABELS: Record<number, (lang: Language) => string> = {
    1: t.portal.dashboard.edit.heightShort,
    2: t.portal.dashboard.edit.heightMedium,
    3: t.portal.dashboard.edit.heightTall,
}

interface CardResizeMenuProps {
    lang: Language
    colSpan?: number
    rowSpan?: number
    onResize: (patch: { colSpan?: number; rowSpan?: number }) => void
}

const CardResizeMenu = ({ lang, colSpan, rowSpan, onResize }: CardResizeMenuProps) => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handle = (e: MouseEvent | KeyboardEvent) => {
            if (e instanceof KeyboardEvent) {
                if (e.key === 'Escape') setOpen(false)
                return
            }
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', handle)
        document.addEventListener('keydown', handle)
        return () => {
            document.removeEventListener('mousedown', handle)
            document.removeEventListener('keydown', handle)
        }
    }, [open])

    return (
        <div className="portal-card__menu" ref={ref}>
            <button
                type="button"
                className="portal-card__menu-trigger"
                aria-label={t.portal.dashboard.edit.moreOptions(lang)}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <MoreHorizontal size={14} weight="Bold" />
            </button>
            {open && (
                <div className="portal-card__menu-popover" role="menu">
                    <div className="portal-card__menu-label">
                        {t.portal.dashboard.edit.width(lang)}
                    </div>
                    {ALLOWED_COL_SPANS.map((span) => (
                        <button
                            key={`w-${span}`}
                            type="button"
                            role="menuitemradio"
                            aria-checked={colSpan === span}
                            className={`portal-card__menu-option${colSpan === span ? ' portal-card__menu-option--active' : ''}`}
                            onClick={() => onResize({ colSpan: span })}
                        >
                            {WIDTH_LABELS[span]?.(lang)}
                        </button>
                    ))}
                    <div className="portal-card__menu-label">
                        {t.portal.dashboard.edit.height(lang)}
                    </div>
                    {ALLOWED_ROW_SPANS.map((span) => (
                        <button
                            key={`h-${span}`}
                            type="button"
                            role="menuitemradio"
                            aria-checked={rowSpan === span}
                            className={`portal-card__menu-option${rowSpan === span ? ' portal-card__menu-option--active' : ''}`}
                            onClick={() => onResize({ rowSpan: span })}
                        >
                            {HEIGHT_LABELS[span]?.(lang)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
